import { noop } from 'lodash';
import { createContext, memo, type ReactNode, useContext, useMemo, useState } from 'react';

import { Portal } from '@air/primitive-portal';

type ActionBarProviderProps = {
  children: ReactNode;
};

type ActionBarProviderContextValue = {
  setActionBar: (component: ReactNode) => void;
  setActionBarXOffset: (offset: number) => void;
  actionBar: ReactNode;
};

const defaultValue: ActionBarProviderContextValue = {
  actionBar: null,
  setActionBar: noop,
  setActionBarXOffset: noop,
};

const ActionBarContext = createContext(defaultValue);

export const ActionBarProvider = memo(({ children }: ActionBarProviderProps) => {
  const [actionBarOffset, setActionBarXOffset] = useState<number>(0);
  const [actionBar, setActionBar] = useState<ReactNode>(null);

  const value = useMemo(
    () => ({
      actionBar,
      setActionBar,
      setActionBarXOffset,
    }),
    [actionBar],
  );

  return (
    <ActionBarContext.Provider value={value}>
      {children}
      {!!actionBar && (
        <Portal>
          <div
            className="pointer-events-none fixed right-0 bottom-0 z-portal m-0 flex touch-none flex-col items-center overflow-hidden px-0 py-3"
            style={{ width: `calc(100% - ${actionBarOffset}px)` }}
          >
            {actionBar}
          </div>
        </Portal>
      )}
    </ActionBarContext.Provider>
  );
});

ActionBarProvider.displayName = 'ActionBarProvider';

export function useActionBar() {
  const context = useContext(ActionBarContext);

  if (context === defaultValue) {
    throw 'useActionBar used outside of ActionBarProvider';
  }

  return context;
}
